import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import LoadingState from "@/components/base/LoadingState";
import TemplateModal, { type ContractTemplateRow, type TemplateForm } from "./components/TemplateModal";
import PreviewModal from "./components/PreviewModal";

/* ─── Language labels ────────────────────────────────────────── */
const LANG: Record<string, string> = {
  en: "EN", es: "ES", pt: "PT", fr: "FR", de: "DE", it: "IT",
};
const LANG_COLOR: Record<string, string> = {
  en: "bg-sky-50 text-sky-600 border-sky-200",
  es: "bg-orange-50 text-orange-600 border-orange-200",
  pt: "bg-emerald-50 text-emerald-600 border-emerald-200",
  fr: "bg-violet-50 text-violet-600 border-violet-200",
  de: "bg-amber-50 text-amber-600 border-amber-200",
  it: "bg-red-50 text-red-600 border-red-200",
};

/* ─── Toast ──────────────────────────────────────────────────── */
function Toast({ msg }: { msg: string }) {
  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-3 bg-white border border-slate-200 rounded-xl shadow-lg">
      <span className="w-4 h-4 flex items-center justify-center text-emerald-500">
        <i className="ri-check-line"></i>
      </span>
      <p className="text-[#0f172a] text-sm">{msg}</p>
    </div>
  );
}

/* ─── Main ───────────────────────────────────────────────────── */
export default function ContractTemplatesPage() {
  const navigate = useNavigate();
  const [tenantId, setTenantId]         = useState<string | null>(null);
  const [templates, setTemplates]       = useState<ContractTemplateRow[]>([]);
  const [loading, setLoading]           = useState(true);
  const [modalOpen, setModalOpen]       = useState(false);
  const [editing, setEditing]           = useState<ContractTemplateRow | null>(null);
  const [previewing, setPreviewing]     = useState<ContractTemplateRow | null>(null);
  const [saving, setSaving]             = useState(false);
  const [deleting, setDeleting]         = useState<string | null>(null);
  const [deleteError, setDeleteError]   = useState<string | null>(null);
  const [toastMsg, setToastMsg]         = useState<string | null>(null);
  const [filter, setFilter]             = useState<"all" | "active" | "inactive">("all");
  const [confirmDelete, setConfirmDelete] = useState<ContractTemplateRow | null>(null);

  const toast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  const fetchTemplates = useCallback(async (tid: string) => {
    const { data } = await supabase
      .from("contract_templates")
      .select("*")
      .eq("tenant_id", tid)
      .order("created_at", { ascending: false });
    setTemplates((data as ContractTemplateRow[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchTemplates(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchTemplates]);

  /* ── Save (create / update) ── */
  const handleSave = async (form: TemplateForm) => {
    if (!tenantId) return;
    setSaving(true);
    const payload = {
      tenant_id:  tenantId,
      name:       form.name.trim(),
      language:   form.language,
      content:    form.content.trim(),
      is_active:  form.is_active,
      updated_at: new Date().toISOString(),
    };
    if (editing) {
      await supabase.from("contract_templates").update(payload).eq("id", editing.id);
      toast("Template updated");
    } else {
      await supabase.from("contract_templates").insert(payload);
      toast("Template created");
    }
    setSaving(false);
    setModalOpen(false);
    setEditing(null);
    fetchTemplates(tenantId);
  };

  /* ── Toggle active ── */
  const handleToggle = async (t: ContractTemplateRow) => {
    if (!tenantId) return;
    await supabase
      .from("contract_templates")
      .update({ is_active: !t.is_active, updated_at: new Date().toISOString() })
      .eq("id", t.id);
    toast(t.is_active ? `"${t.name}" deactivated` : `"${t.name}" activated`);
    fetchTemplates(tenantId);
  };

  /* ── Duplicate ── */
  const handleDuplicate = async (t: ContractTemplateRow) => {
    if (!tenantId) return;
    await supabase.from("contract_templates").insert({
      tenant_id:  tenantId,
      name:       `${t.name} (copy)`,
      language:   t.language,
      content:    t.content,
      is_active:  false,
      updated_at: new Date().toISOString(),
    });
    toast(`"${t.name}" duplicated`);
    fetchTemplates(tenantId);
  };

  /* ── Delete: check if used in contracts first ── */
  const initiateDelete = async (t: ContractTemplateRow) => {
    setDeleteError(null);
    // Check if template is used
    const { count } = await supabase
      .from("contracts")
      .select("id", { count: "exact", head: true })
      .eq("tenant_id", tenantId ?? "")
      .eq("template_id", t.id);

    if ((count ?? 0) > 0) {
      setDeleteError(`Cannot delete "${t.name}" — it's used in ${count} contract${(count ?? 0) > 1 ? "s" : ""}. Deactivate it instead.`);
      setTimeout(() => setDeleteError(null), 5000);
      return;
    }
    setConfirmDelete(t);
  };

  const handleDelete = async () => {
    if (!confirmDelete || !tenantId) return;
    setDeleting(confirmDelete.id);
    await supabase.from("contract_templates").delete().eq("id", confirmDelete.id).eq("tenant_id", tenantId);
    setDeleting(null);
    toast(`"${confirmDelete.name}" deleted`);
    setConfirmDelete(null);
    fetchTemplates(tenantId);
  };

  /* ── Filtered list ── */
  const filtered = filter === "all"
    ? templates
    : templates.filter((t) => filter === "active" ? t.is_active : !t.is_active);

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit   = (t: ContractTemplateRow) => { setEditing(t); setModalOpen(true); };

  return (
    <AdminLayout>
      <AdminHeader
        title="Contract Templates"
        subtitle="Reusable templates with dynamic variables for professional contracts"
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/admin/contracts"
              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              All Contracts
            </Link>
            <button
              onClick={openCreate}
              className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
              New Template
            </button>
          </div>
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7">

        {/* Delete error banner */}
        {deleteError && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-5">
            <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0"></i>
            <p className="text-red-700 text-sm flex-1">{deleteError}</p>
            <button onClick={() => setDeleteError(null)} className="text-red-400 hover:text-red-600 cursor-pointer">
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        )}

        {loading ? (
          <LoadingState />
        ) : templates.length === 0 ? (
          /* ── Empty state ── */
          <div className="flex flex-col items-center justify-center py-24 max-w-sm mx-auto text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[#C8A96E]/10 mb-5">
              <i className="ri-file-text-line text-[#C8A96E] text-3xl"></i>
            </div>
            <h3 className="text-[#0f172a] font-semibold text-base mb-2">No templates yet</h3>
            <p className="text-slate-400 text-sm mb-6 leading-relaxed">
              Create professional contract templates with dynamic variables like{" "}
              <code className="text-[#C8A96E] bg-[#C8A96E]/8 px-1 rounded text-xs">{"{{client_name}}"}</code>
              {" "}and{" "}
              <code className="text-[#C8A96E] bg-[#C8A96E]/8 px-1 rounded text-xs">{"{{event_date}}"}</code>.
              Reuse them for any booking.
            </p>
            <button
              onClick={openCreate}
              className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Create First Template
            </button>
          </div>
        ) : (
          <>
            {/* Stats + filter row */}
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
                {([
                  { key: "all",      label: "All",      count: templates.length },
                  { key: "active",   label: "Active",   count: templates.filter((t) => t.is_active).length },
                  { key: "inactive", label: "Inactive", count: templates.filter((t) => !t.is_active).length },
                ] as const).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setFilter(tab.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      filter === tab.key
                        ? "bg-[#C8A96E] text-white"
                        : "text-slate-400 hover:text-[#0f172a]"
                    }`}
                  >
                    {tab.label}
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${
                      filter === tab.key ? "bg-white/20 text-white" : "bg-slate-100 text-slate-400"
                    }`}>{tab.count}</span>
                  </button>
                ))}
              </div>
              <p className="text-slate-400 text-xs">
                {filtered.length} template{filtered.length !== 1 ? "s" : ""}
              </p>
            </div>

            {/* Template list */}
            <div className="space-y-3 max-w-4xl">
              {filtered.map((t) => (
                <div
                  key={t.id}
                  className={`bg-white rounded-xl border transition-all ${
                    t.is_active ? "border-slate-200 hover:border-[#C8A96E]/30" : "border-slate-100 opacity-60"
                  }`}
                >
                  <div className="flex items-center gap-4 px-5 py-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${
                      t.is_active ? "bg-[#C8A96E]/10" : "bg-slate-100"
                    }`}>
                      <i className={`ri-file-text-line text-base ${t.is_active ? "text-[#C8A96E]" : "text-slate-300"}`}></i>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-[#0f172a] font-semibold text-sm">{t.name}</p>
                        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold border ${LANG_COLOR[t.language] ?? "bg-slate-50 text-slate-400 border-slate-200"}`}>
                          {LANG[t.language] ?? t.language.toUpperCase()}
                        </span>
                        {!t.is_active && (
                          <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[9px] bg-slate-100 text-slate-400 border border-slate-200">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-slate-400 text-xs flex-wrap">
                        <span>{t.content.split("\n").length} lines</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                        <span>{t.content.length.toLocaleString()} chars</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                        <span>Updated {new Date(t.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                        {/* Variable count */}
                        {(() => {
                          const vars = (t.content.match(/\{\{[^}]+\}\}/g) ?? []);
                          const unique = [...new Set(vars)].length;
                          return unique > 0 ? (
                            <>
                              <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                              <span className="text-[#C8A96E]">{unique} variable{unique !== 1 ? "s" : ""}</span>
                            </>
                          ) : null;
                        })()}
                      </div>
                    </div>

                    {/* Active toggle */}
                    <button
                      type="button"
                      onClick={() => handleToggle(t)}
                      title={t.is_active ? "Deactivate" : "Activate"}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer shrink-0 ${
                        t.is_active ? "bg-[#C8A96E]" : "bg-slate-200"
                      }`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                        t.is_active ? "translate-x-4" : "translate-x-0.5"
                      }`} />
                    </button>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => setPreviewing(t)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-300 hover:text-[#C8A96E] hover:bg-[#C8A96E]/8 transition-all cursor-pointer"
                        title="Preview"
                      >
                        <i className="ri-eye-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => openEdit(t)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-300 hover:text-[#0f172a] hover:bg-slate-100 transition-all cursor-pointer"
                        title="Edit"
                      >
                        <i className="ri-edit-2-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => handleDuplicate(t)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-300 hover:text-[#0f172a] hover:bg-slate-100 transition-all cursor-pointer"
                        title="Duplicate"
                      >
                        <i className="ri-file-copy-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => initiateDelete(t)}
                        disabled={deleting === t.id}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all cursor-pointer disabled:opacity-40"
                        title="Delete"
                      >
                        {deleting === t.id
                          ? <div className="w-3.5 h-3.5 border border-red-400 border-t-transparent rounded-full animate-spin" />
                          : <i className="ri-delete-bin-6-line text-sm"></i>
                        }
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Delete confirm dialog ── */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 flex-shrink-0">
                <i className="ri-delete-bin-line text-red-500 text-lg"></i>
              </div>
              <div>
                <p className="text-[#0f172a] font-semibold text-sm">Delete template?</p>
                <p className="text-slate-400 text-xs mt-0.5">This action cannot be undone.</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg px-4 py-3">
              <p className="text-[#0f172a] text-sm font-medium">{confirmDelete.name}</p>
              <p className="text-slate-400 text-xs mt-0.5">{confirmDelete.content.length.toLocaleString()} chars · {confirmDelete.language.toUpperCase()}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={!!deleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {deleting ? <><i className="ri-loader-4-line animate-spin"></i> Deleting…</> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <TemplateModal
        open={modalOpen}
        editing={editing}
        saving={saving}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSave={handleSave}
      />
      <PreviewModal
        template={previewing}
        onClose={() => setPreviewing(null)}
        onEdit={(t) => { setPreviewing(null); openEdit(t); }}
      />

      {toastMsg && <Toast msg={toastMsg} />}
    </AdminLayout>
  );
}